/**
 * Unified store transaction ledger — one row per POSTransaction with
 * attribution_status showing how each sale was identified.
 *
 * The `unattached_card` count is the diagnostic signal: any non-zero
 * value on a given day means the Helcim email pipeline missed that
 * day's card sales. Drill via the pipeline-health page to confirm
 * what failed.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

type Status =
  | 'bc_member'
  | 'wholesale'
  | 'named_walkin'
  | 'anonymous_card'
  | 'cash'
  | 'check'
  | 'unattached_card';

interface Row {
  id: number;
  transaction_number: string;
  completed_at: string | null;
  cashier_name: string;
  subtotal: string;
  tax_amount: string;
  surcharge_amount: string;
  total: string;
  payment_method: string;
  card_last_four: string;
  attribution_status: Status;
  attribution_label: string;
  attribution_link: string;
  item_count: number;
  top_item: string;
}

interface Rollup {
  date: string;
  total_count: number;
  total_revenue: string;
  by_status: Record<Status, number>;
}

interface LedgerData {
  date_from: string;
  date_to: string;
  rows: Row[];
  rollup: Rollup;
}

const STATUS_COLOR: Record<Status, { bg: string; fg: string }> = {
  bc_member: { bg: '#dbeafe', fg: '#1e40af' },
  wholesale: { bg: '#ccfbf1', fg: '#0f766e' },
  named_walkin: { bg: '#ffedd5', fg: '#c2410c' },
  anonymous_card: { bg: '#f3f4f6', fg: '#374151' },
  cash: { bg: '#dcfce7', fg: '#166534' },
  check: { bg: '#dcfce7', fg: '#166534' },
  unattached_card: { bg: '#fee2e2', fg: '#991b1b' },
};

const STATUS_ORDER: Status[] = [
  'bc_member',
  'wholesale',
  'named_walkin',
  'anonymous_card',
  'cash',
  'check',
  'unattached_card',
];

const fmtMoney = (s: string) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
    parseFloat(s || '0'),
  );

const StoreLedger: React.FC = () => {
  const navigate = useNavigate();
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [filter, setFilter] = useState<Status | 'all'>('all');
  const [data, setData] = useState<LedgerData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<LedgerData>('/admin/bridge-club/store-ledger/', {
        params: { date_from: dateFrom, date_to: dateTo },
      });
      setData(res.data);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredRows = useMemo(() => {
    if (!data) return [];
    if (filter === 'all') return data.rows;
    return data.rows.filter((r) => r.attribution_status === filter);
  }, [data, filter]);

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>
        Store Ledger
      </h1>
      <div style={{ color: '#6b7280', fontSize: 14, marginBottom: 20 }}>
        Every till transaction with attribution status. Red rows are card sales
        with no cardholder name — usually a tapped card. Nothing to recover.
      </div>

      {/* Controls */}
      <div
        style={{
          display: 'flex',
          gap: 12,
          alignItems: 'end',
          marginBottom: 20,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <label
            style={{ display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 4 }}
          >
            From
          </label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6 }}
          />
        </div>
        <div>
          <label
            style={{ display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 4 }}
          >
            To
          </label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6 }}
          />
        </div>
        <div>
          <label
            style={{ display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 4 }}
          >
            Filter
          </label>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as Status | 'all')}
            style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6 }}
          >
            <option value="all">All</option>
            {STATUS_ORDER.map((s) => (
              <option key={s} value={s}>
                {s.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        </div>
        <button
          onClick={load}
          style={{
            background: '#059669',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: 6,
            cursor: 'pointer',
            minHeight: 38,
          }}
        >
          Reload
        </button>
      </div>

      {loading && <div style={{ padding: 12 }}>Loading…</div>}
      {error && (
        <div style={{ padding: 12, color: '#dc2626' }}>Error: {error}</div>
      )}

      {/* Rollup */}
      {data && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: 12,
            marginBottom: 24,
          }}
        >
          <div
            style={{
              background: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              padding: 16,
            }}
          >
            <div style={{ color: '#6b7280', fontSize: 12 }}>Total sales</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>
              {data.rollup.total_count}
            </div>
            <div style={{ color: '#6b7280', fontSize: 13 }}>
              {fmtMoney(data.rollup.total_revenue)}
            </div>
          </div>
          {STATUS_ORDER.map((s) => {
            const c = STATUS_COLOR[s];
            const n = data.rollup.by_status[s] || 0;
            return (
              <div
                key={s}
                onClick={() => setFilter(filter === s ? 'all' : s)}
                style={{
                  background: c.bg,
                  border:
                    filter === s ? `2px solid ${c.fg}` : '1px solid transparent',
                  borderRadius: 8,
                  padding: 16,
                  cursor: 'pointer',
                }}
              >
                <div style={{ color: c.fg, fontSize: 11, fontWeight: 600 }}>
                  {s.replace(/_/g, ' ').toUpperCase()}
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, color: c.fg }}>
                  {n}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Rows */}
      {data && (
        <div
          style={{
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            overflow: 'auto',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f9fafb', textAlign: 'left' }}>
                <th style={{ padding: 10 }}>Time</th>
                <th style={{ padding: 10 }}>Txn #</th>
                <th style={{ padding: 10 }}>Cashier</th>
                <th style={{ padding: 10 }}>Items</th>
                <th style={{ padding: 10 }}>Top item</th>
                <th style={{ padding: 10, textAlign: 'right' }}>Total</th>
                <th style={{ padding: 10 }}>Payment</th>
                <th style={{ padding: 10 }}>Attribution</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((r) => {
                const c = STATUS_COLOR[r.attribution_status];
                return (
                  <tr
                    key={r.id}
                    onClick={() => navigate(r.attribution_link)}
                    style={{
                      borderTop: '1px solid #f3f4f6',
                      cursor: 'pointer',
                      background:
                        r.attribution_status === 'unattached_card'
                          ? '#fef2f2'
                          : 'transparent',
                    }}
                  >
                    <td style={{ padding: 10 }}>
                      {r.completed_at
                        ? new Date(r.completed_at).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : '—'}
                    </td>
                    <td style={{ padding: 10, fontFamily: 'monospace' }}>
                      {r.transaction_number}
                    </td>
                    <td style={{ padding: 10 }}>{r.cashier_name}</td>
                    <td style={{ padding: 10 }}>{r.item_count}</td>
                    <td
                      style={{
                        padding: 10,
                        maxWidth: 200,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {r.top_item}
                    </td>
                    <td style={{ padding: 10, textAlign: 'right', fontWeight: 600 }}>
                      {fmtMoney(r.total)}
                    </td>
                    <td style={{ padding: 10 }}>
                      {r.payment_method}
                      {r.card_last_four ? ` ••${r.card_last_four}` : ''}
                    </td>
                    <td style={{ padding: 10 }}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '3px 8px',
                          borderRadius: 4,
                          fontSize: 11,
                          fontWeight: 600,
                          background: c.bg,
                          color: c.fg,
                        }}
                      >
                        {r.attribution_label}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {filteredRows.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>
                    No transactions in the selected range/filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default StoreLedger;
